import type { VideoGenerationProviderChannel } from '../../../api'
import {
  channelDurationChoices,
  channelQualityChoices,
  channelRatioChoices,
} from './videoGenerationProvider'

export type VideoGenerationOperation = 'TEXT_TO_VIDEO' | 'IMAGE_TO_VIDEO'

export type ParameterAdjustment = {
  field: 'aspect_ratio' | 'duration_seconds' | 'quality_tier'
  fromValue: string
  toValue: string
}

export function resolveVideoGenerationOperation(args: {
  hasKeyframeImage: boolean
  hasInputAssetImage: boolean
  hasReferenceImageAsset: boolean
}): VideoGenerationOperation {
  if (args.hasKeyframeImage || args.hasInputAssetImage || args.hasReferenceImageAsset) {
    return 'IMAGE_TO_VIDEO'
  }
  return 'TEXT_TO_VIDEO'
}

function pickAspectRatio(current: string, allowed: string[]): string {
  if (!allowed.length) return current
  if (allowed.includes('16:9')) return '16:9'
  return allowed[0]
}

function pickDuration(current: number, allowed: number[]): number {
  if (!allowed.length) return current
  return allowed.reduce((best, value) => {
    const bestDistance = Math.abs(best - current)
    const valueDistance = Math.abs(value - current)
    if (valueDistance < bestDistance) return value
    if (valueDistance > bestDistance) return best
    return value < best ? value : best
  }, allowed[0])
}

export function normalizeVideoGenerationParameters(args: {
  channel: VideoGenerationProviderChannel | null
  operation: VideoGenerationOperation
  aspectRatio: string
  durationSeconds: number
  qualityTier: string
  fallbackRatios: string[]
  fallbackDurations: number[]
  fallbackQuality: { value: string; label: string }[]
}): {
  aspectRatio: string
  durationSeconds: number
  qualityTier: string
  adjustments: ParameterAdjustment[]
} {
  const ratioChoices = channelRatioChoices(args.channel, args.fallbackRatios)
  const durationChoices = channelDurationChoices(args.channel, args.fallbackDurations, args.qualityTier)
  const qualityChoices = channelQualityChoices(args.channel, args.fallbackQuality)
  const adjustments: ParameterAdjustment[] = []

  let aspectRatio = args.aspectRatio
  if (ratioChoices.length && !ratioChoices.includes(aspectRatio)) {
    const next = pickAspectRatio(aspectRatio, ratioChoices)
    adjustments.push({ field: 'aspect_ratio', fromValue: aspectRatio, toValue: next })
    aspectRatio = next
  }

  let qualityTier = args.qualityTier
  const qualityValues = qualityChoices.map((choice) => choice.value)
  if (qualityValues.length && !qualityValues.includes(qualityTier)) {
    const next = qualityValues[0]
    adjustments.push({ field: 'quality_tier', fromValue: qualityTier, toValue: next })
    qualityTier = next
  }

  let durationSeconds = args.durationSeconds
  if (durationChoices.length && !durationChoices.includes(durationSeconds)) {
    const next = pickDuration(durationSeconds, durationChoices)
    adjustments.push({ field: 'duration_seconds', fromValue: String(durationSeconds), toValue: String(next) })
    durationSeconds = next
  }

  return { aspectRatio, durationSeconds, qualityTier, adjustments }
}

export function formatParameterAdjustmentHint(
  adjustments: ParameterAdjustment[],
  t: (key: string, options?: Record<string, unknown>) => string,
): string | null {
  if (!adjustments.length) return null
  if (adjustments.length === 1) {
    const adjustment = adjustments[0]
    if (adjustment.field === 'quality_tier') {
      const label = adjustments[0].toValue === 'Normal' ? '768P' : adjustment.toValue
      return t('generateVideo.parameterAdjustedResolution', { resolution: label })
    }
    if (adjustment.field === 'duration_seconds') {
      return t('generateVideo.parameterAdjustedDuration', { seconds: adjustment.toValue })
    }
    if (adjustment.field === 'aspect_ratio') {
      return t('generateVideo.parameterAdjustedRatio', { ratio: adjustment.toValue })
    }
  }
  return t('generateVideo.parameterAdjustedMultiple')
}
